import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame } from "lucide-react";
import { COAL_VARIETIES } from "@/lib/coal-data";

export default function Products() {
    const [active, setActive] = useState(COAL_VARIETIES[0].slug);
    const current = COAL_VARIETIES.find((c) => c.slug === active);

    return (
        <div data-testid="page-products">
            {/* HERO */}
            <section className="pt-40 pb-20 bg-coal-900 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-6">
                        Product Catalogue · {COAL_VARIETIES.length} Varieties
                    </div>
                    <h1 className="font-display font-bold text-5xl sm:text-7xl uppercase tracking-tighter leading-[0.92] text-white max-w-5xl">
                        Every major coal,
                        <br />
                        every major origin.
                    </h1>
                    <p className="mt-8 max-w-2xl text-lg text-zinc-300 leading-relaxed">
                        From anthracite to lignite, coking to thermal, pet coke
                        to met coke — the full spectrum of solid fuels traded
                        globally, calibrated to Indian industrial demand.
                    </p>
                </div>
            </section>

            {/* INTERACTIVE EXPLORER */}
            <section className="section bg-coal-900">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-px bg-white/10 border border-white/10">
                    {/* List */}
                    <aside className="lg:col-span-4 bg-coal-800 p-3 lg:max-h-[640px] overflow-y-auto">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold p-4">
                            Select a variety
                        </div>
                        <ul>
                            {COAL_VARIETIES.map((c) => (
                                <li key={c.slug}>
                                    <button
                                        onClick={() => setActive(c.slug)}
                                        data-testid={`coal-tab-${c.slug}`}
                                        className={`w-full text-left p-4 border-l-2 transition-all flex items-baseline justify-between gap-4 ${
                                            active === c.slug
                                                ? "bg-coal-900 border-ember text-white"
                                                : "border-transparent text-zinc-300 hover:bg-coal-700 hover:text-white"
                                        }`}
                                    >
                                        <span className="font-display text-sm font-semibold leading-snug">
                                            {c.name}
                                        </span>
                                        <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider shrink-0">
                                            {c.tag}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </aside>

                    {/* Detail */}
                    <div
                        className="lg:col-span-8 bg-coal-900 p-8 lg:p-12"
                        data-testid="coal-detail"
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <Flame className="w-5 h-5 text-ember" />
                            <span className="text-[10px] tracking-[0.25em] uppercase text-ember font-bold">
                                {current.tag}
                            </span>
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-[1]">
                            {current.name}
                        </h2>
                        <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-3xl">
                            {current.description}
                        </p>

                        {/* Origins */}
                        <div className="mt-8 flex flex-wrap gap-2">
                            {current.origin.map((o) => (
                                <span
                                    key={o}
                                    className="text-xs tracking-widest uppercase text-zinc-300 border border-white/15 px-3 py-1.5 font-mono"
                                >
                                    {o}
                                </span>
                            ))}
                        </div>

                        {/* Spec grid */}
                        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                            <Spec label="GCV" value={current.gcv} />
                            <Spec label="Fixed Carbon" value={current.carbon} />
                            <Spec label="Ash" value={current.ash} />
                            <Spec label="Moisture" value={current.moisture} />
                            <Spec label="Sulfur" value={current.sulfur} />
                            <Spec label="Volatile" value={current.volatile} />
                        </div>

                        {/* Uses */}
                        <div className="mt-10">
                            <div className="text-xs tracking-[0.25em] uppercase text-zinc-500 font-bold mb-4">
                                Typical Applications
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {current.uses.map((u) => (
                                    <span
                                        key={u}
                                        className="bg-coal-800 border border-white/10 text-zinc-200 px-4 py-2 text-sm"
                                    >
                                        {u}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="mt-12 flex flex-wrap gap-4">
                            <Link
                                to="/contact"
                                data-testid="product-cta-enquire"
                                className="bg-ember hover:bg-ember-hover text-white px-7 py-4 font-bold tracking-[0.18em] uppercase text-sm transition-colors flex items-center gap-3"
                            >
                                Request Quote <ArrowRight className="w-4 h-4" />
                            </Link>
                            <a
                                href="mailto:carbexinternational@gmail.com?subject=Spec sheet request"
                                className="bg-transparent border border-white/20 hover:border-white text-white px-7 py-4 font-bold tracking-[0.18em] uppercase text-sm transition-colors"
                            >
                                Email Spec Sheet
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* COMPARISON TABLE */}
            <section className="section bg-coal-800 border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                    <div className="text-xs tracking-[0.25em] uppercase text-ember font-bold mb-4">
                        Side-by-side
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white">
                        Technical Spec Index
                    </h2>
                    <div className="mt-10 overflow-x-auto border border-white/10">
                        <table className="w-full text-sm font-mono">
                            <thead>
                                <tr className="bg-coal-900 text-zinc-500 uppercase tracking-wider text-[10px]">
                                    <th className="text-left p-4 border-r border-white/10">
                                        Variety
                                    </th>
                                    <th className="text-left p-4 border-r border-white/10">
                                        GCV
                                    </th>
                                    <th className="text-left p-4 border-r border-white/10">
                                        Ash
                                    </th>
                                    <th className="text-left p-4 border-r border-white/10">
                                        Moist.
                                    </th>
                                    <th className="text-left p-4 border-r border-white/10">
                                        Sulfur
                                    </th>
                                    <th className="text-left p-4">Origin</th>
                                </tr>
                            </thead>
                            <tbody>
                                {COAL_VARIETIES.map((c, i) => (
                                    <tr
                                        key={c.slug}
                                        className={`${
                                            i % 2 === 0
                                                ? "bg-coal-900"
                                                : "bg-coal-800"
                                        } text-zinc-200 hover:bg-coal-700 transition-colors`}
                                    >
                                        <td className="p-4 border-r border-white/5 font-sans">
                                            <button
                                                className="text-left text-white hover:text-ember font-medium"
                                                onClick={() => {
                                                    setActive(c.slug);
                                                    window.scrollTo({
                                                        top: 320,
                                                        behavior: "smooth",
                                                    });
                                                }}
                                            >
                                                {c.name}
                                            </button>
                                        </td>
                                        <td className="p-4 border-r border-white/5">
                                            {c.gcv}
                                        </td>
                                        <td className="p-4 border-r border-white/5">
                                            {c.ash}
                                        </td>
                                        <td className="p-4 border-r border-white/5">
                                            {c.moisture}
                                        </td>
                                        <td className="p-4 border-r border-white/5">
                                            {c.sulfur}
                                        </td>
                                        <td className="p-4 text-zinc-400">
                                            {c.origin.join(", ")}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>
        </div>
    );
}

function Spec({ label, value }) {
    return (
        <div className="bg-coal-900 p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 font-bold mb-2">
                {label}
            </div>
            <div className="font-mono text-white text-base">{value}</div>
        </div>
    );
}
